import { ServiceLayout } from "@/components/ServiceLayout"
import { algorithmicCommerceFAQs } from "@/data/faqData"

const AlgorithmicCommerce = () => {
  return (
    <ServiceLayout
      title="Algorithmic Commerce"
      description="E-commerce platforms driven by recommendation engines and dynamic pricing algorithms to maximize revenue per session."
      heroImage="https://images.unsplash.com/photo-1556742049-0cfed4f7a07d?auto=format&fit=crop&q=80&w=2600"
      faqs={algorithmicCommerceFAQs}
      faqTitle="Algorithmic Commerce FAQs"
      faqSubtitle="Common questions about AI-powered e-commerce and dynamic pricing"
      features={[
        {
          title: "Dynamic Pricing Engines",
          description:
            "Algorithms that adjust prices in real-time based on demand, competitor data, and inventory levels to maximize margin.",
        },
        {
          title: "Hyper-Personalized Search",
          description:
            "Search results that re-rank automatically based on the user's purchase history and browsing intent.",
        },
        {
          title: "Visual AI Search",
          description:
            "Allowing users to search for products using images, powered by computer vision models.",
        },
      ]}
      approach={{
        title: "Probabilistic Conversion Engines",
        description:
          "Optimizing the 'Path to Purchase' with mathematical precision.",
        image:
          "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=2600",
        content: [
          "We treat commerce not as a catalog, but as a mathematical optimization problem. We map the entire 'Conversion Funnel' as a probabilistic graph, identifying where user intent drops off.",
          "To plug these leaks, we inject 'Dynamic Pricing Neurons' - algorithms that adjust pricing strategies in real-time based on inventory levels, competitor data, and demand elasticity to maximize margin without sacrificing volume.",
          "We decouple the frontend from the backend (Headless Commerce), allowing for millisecond page loads and fluid, app-like experiences. This speed, combined with hyper-personalized product ranking, dramatically increases Average Order Value (AOV).",
        ],
      }}
      benefits={[
        "15% Increase in AOV",
        "Real-Time Inventory Sync",
        "Cart Abandonment Recovery",
        "Omnichannel Loyalty Programs",
        "Automated Merchandising",
        "Fraud Detection AI",
      ]}
      process={{
        steps: [
          {
            title: "Data Unification",
            description:
              "Consolidating customer data from POS, web, mobile, and social channels into a single Customer Data Platform (CDP).",
          },
          {
            title: "Algorithm Training",
            description:
              "Feeding historical purchase data into our recommendation and pricing engines to identify patterns and elasticity.",
          },
          {
            title: "UX Integration",
            description:
              "Embedding personalized widgets (e.g., 'You May Also Like', 'Recently Viewed') seamlessly into the shopping experience.",
          },
          {
            title: "Performance Tuning",
            description:
              "Running multivariate tests on pricing strategies and recommendation algorithms to maximize conversion value.",
          },
        ],
      }}
      techStack={[
        {
          category: "Commerce Platforms",
          items: ["Shopify Plus", "Magento", "BigCommerce", "commercetools"],
        },
        {
          category: "Search & Discovery",
          items: ["Algolia", "Elasticsearch", "Constructor.io"],
        },
        {
          category: "Data & Analytics",
          items: ["Segment", "Google Analytics 4", "Looker", "Heap"],
        },
      ]}
    />
  )
}

export default AlgorithmicCommerce
